<template>
  <div class="filters-accordion">
    <div class="head">
      Filter by
    </div>
    <div class="filters-dropdown">
      <div
        v-for="(filter, idx) of f" :id="`filter-${idx}`" :key="idx"
        :class="[filter.open ? 'open' : '']"
      >
        <div @click="filter.open = !filter.open">
          <span class="filter-name">
            {{ filter.name }}
          </span>
          <IconArrowDown />
        </div>
        <div v-if="filter.type === 'text'" class="options search-options">
          <input
            type="text"
            :placeholder="filter.values[idx].text"
            :value="filter.values[idx].value"
            :name="idx"
            @input="input"
            @keyup="search"
            @keydown="search"
          >
          <div v-if="searchResults.length && filter.values[idx].value" class="search-results">
            <ul>
              <li
                v-for="result, i in searchResults"
                :key="i"
                :data-value="result.value"
                :data-idx="idx"
                :class="{ focused: filter.selectedIdx === i }"
                @click="selectResultOption"
              >
                {{ result.value }}
              </li>
            </ul>
          </div>
        </div>
        <div v-else-if="filter.type === 'range'" class="options range-options">
          <div>
            <input
              :value="filter.values[idx].value[0]"
              type="text"
              class="min-range-input"
              @keydown="validateNumber"
              @input="rangeChange(idx, [+$event.target.value, +filter.values[idx].value[1]])"
            >
            <input
              :value="filter.values[idx].value[1]"
              type="text"
              class="max-range-input"
              @keydown="validateNumber"
              @keyup="validateMaxRange(idx, $event)"
              @input="rangeChange(idx, [+filter.values[idx].value[0], +$event.target.value])"
            >
          </div>
          <div class="range-slider-wrapper">
            <client-only>
              <VueSlider
                :value="filter.values[idx].defaultValue"
                :enable-cross="false"
                tooltip="none"
                :lazy="true"
                :min="filter.values[idx].min"
                :max="filter.values[idx].max"
                :interval="filter.values[idx].step"
                @change="rangeChange(idx, $event)"
                @dragging="rangeDragging(idx, $event)"
              />
            </client-only>
          </div>
        </div>
        <ul v-else-if="filter.values" class="options" :class="{ 'show-more': showMore(filter.values) }">
          <li
            v-for="(v, k) of filter.values"
            :key="k"
            :class="{ selected: v.selected, first: v.first, last: v.last, 'not-capitalized': idx === 'duration' }"
            :title="v.text"
            :style="{ order: v.i }"
            @click="selectOption(idx, k)"
          >
            <span>{{ v.text }}</span>
          </li>
        </ul>
        <div v-if="showMore(filter.values)" class="show" @click="expand($event)" />
      </div>
    </div>
  </div>
</template>

<script>
import { cloneDeep, debounce } from 'lodash-es';
import 'vue-slider-component/theme/default.css';

import IconArrowDown from '../assets/svg/icon-arrow-expand.svg?vue-component';

export default {
  components: {
    IconArrowDown,
    // eslint-disable-next-line consistent-return
    VueSlider: () => {
      if (process.client) {
        return import('vue-slider-component');
      }
    },
  },
  props: {
    filters: { type: Object, required: true },
    searchResults: { type: Array, required: false, default: () => ([]) },
    talentPoolFilters: { type: Boolean, required: false },
    deSelected: { type: Object, required: false, default: () => ({}) },
    reset: { type: Boolean, required: true },
  },
  data() {
    return {
      f: cloneDeep(this.filters),
      defaultFilters: cloneDeep(this.filters),
      open: false,
      noFiltersApplied: true,
    };
  },
  computed: {
    appliedFilters() {
      return this.$getAppliedFilters(this.f);
    },
    filterParams() {
      return this.$getFilterParams(this.appliedFilters, this.f);
    },
    haveAppliedFilters() {
      return Object.values(this.appliedFilters).some((f) => f.length);
    },
  },
  watch: {
    filters(v) {
      let textFilterApplied = false;
      let rangeFilterApplied = false;
      let multipleFilterApplied = false;

      // eslint-disable-next-line no-restricted-syntax
      for (const [filter, data] of Object.entries(v)) {
        if (data.type === 'text') {
          const [filterValue] = data.values[filter].value;
          textFilterApplied = Boolean(filterValue);
        }

        if (data.type === 'range') {
          rangeFilterApplied = +data.values[filter].min !== +data.values[filter].value[0] || +data.values[filter].max !== +data.values[filter].value[1];
        }
        if (data.type === 'multiple') {
        // eslint-disable-next-line
          Object.values(data.values).forEach((d) => {
            if (d.selected) {
              multipleFilterApplied = true;
            }
          });
        }
      }

      this.noFiltersApplied = !textFilterApplied && !rangeFilterApplied && !multipleFilterApplied;
      this.f = cloneDeep(v);
    },
    deSelected(s) {
      this.selectOption(s.type, s.filter);
    },
    reset(r) {
      if (r) {
        this.clear();
      }
    },
  },
  methods: {
    selectOption(idx, k) {
      const filter = this.f[idx];
      const { selected } = filter.values[k];
      if (filter.type !== 'multiple') {
        Object.keys(this.f[idx].values).filter((f) => f !== k).forEach((f) => {
          this.$set(this.f[idx].values[f], 'selected', false);
        });
      }

      this.$set(this.f[idx].values[k], 'selected', !selected);

      if (this.f[idx].values[k].all) {
        Object.keys(this.f[idx].values).filter((f) => f !== k).forEach((f) => {
          this.$set(this.f[idx].values[f], 'selected', this.f[idx].values[k].selected);
        });
      }

      if (this.$store.state.layout === 'mobile') {
        return;
      }

      this.$emit('change', {
        current: {
          key: idx,
          value: Object.entries(this.f[idx].values).filter(([, v]) => v.selected || v.value).map(([_k]) => _k),
        },
        filter: this.appliedFilters,
        params: this.filterParams,
      });
    },
    input: debounce(function input(e) {
      const k = e.target.name;
      const v = e.target.value;

      this.$emit('change', { current: { key: k, value: v }, filter: this.appliedFilters, params: this.filterParams });
    }, 1000),
    search(e) {
      const k = e.target.name;
      const v = e.target.value;

      this.$set(this.f[k].values[k], 'value', v);

      let idx = this.f[k].selectedIdx;
      if (e.code === 'Enter' || e.code === 'NumpadEnter' || e.code === 'ArrowDown' || e.code === 'ArrowUp') {
        if (e.target.nextElementSibling && e.target.nextElementSibling.classList.contains('search-results')) {
          const results = e.target.nextElementSibling.querySelectorAll('li');
          e.preventDefault();
          if (e.code === 'ArrowDown') {
            if (!idx && idx !== 0) {
              idx = 0;
            } else if (idx < results.length - 1) {
              idx += 1;
            }

            results[idx].scrollIntoView({ block: 'nearest', inline: 'nearest' });
          } else if (e.code === 'ArrowUp') {
            if (idx > 0) {
              idx -= 1;
              results[idx].scrollIntoView({ block: 'nearest', inline: 'nearest' });
            }
          } else if (e.code === 'Enter' || e.code === 'NumpadEnter') {
            results[idx].click();
          }
          this.$set(this.f[k], 'selectedIdx', idx);
        }
      } else {
        this.$emit('type', k);
        this.$set(this.f[k], 'selectedIdx', null);
      }
    },
    selectResultOption(e) {
      const k = e.target.dataset.idx;
      const v = e.target.dataset.value;

      this.$set(this.f[k].values[k], 'value', v);

      this.$emit('change', { current: { key: k, value: v, selected: true }, filter: this.appliedFilters, params: this.filterParams });
    },
    clean() {
      this.f = cloneDeep(this.filters);

      this.$emit('change', { filter: this.appliedFilters, params: this.filterParams });
      this.$emit('clean');
    },
    rangeChange: debounce(function rangeChange(idx, v) {
      const [, max] = v;

      if (max <= this.f[idx].values[idx].max) {
        this.$set(this.f[idx].values[idx], 'value', v);
        this.$emit('change', { current: { key: idx, value: v }, filter: this.appliedFilters, params: this.filterParams });
      }
    }, 1000),
    validateNumber(e) {
      const v = +e.key;

      if (!v && e.key !== 'Backspace' && e.key !== 'Delete' && v !== 0 && e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') {
        e.preventDefault();
      }
    },
    validateMaxRange(idx, e) {
      const { max } = this.f[idx].values[idx];
      if (e.target.value > max) {
        // eslint-disable-next-line no-param-reassign
        e.target.value = max;
        this.$set(this.f[idx].values[idx].value, 1, max);
      }
    },
    clear() {
      // eslint-disable-next-line no-unused-vars
      Object.entries(this.f).forEach(([k, v]) => {
        Object.entries(this.f[k].values).forEach(([_k, _v]) => {
          if ('selected' in _v) {
            // eslint-disable-next-line no-underscore-dangle
            this.f[k].values[_k].selected = false;
          }

          if ('value' in _v && this.f[k].type === 'text') {
            // eslint-disable-next-line no-underscore-dangle
            this.f[k].values[_k].value = '';
          }

          if ('value' in _v && this.f[k].type === 'range') {
            // eslint-disable-next-line no-underscore-dangle
            this.f[k].values[_k].value = [this.f[k].values[_k].min, this.f[k].values[_k].max];
          }
        });
      });
      this.f = cloneDeep(this.f);

      if (this.f.location) {
        this.f.location.values.location.value = '';
      }

      this.$nextTick(() => {
        this.$emit('change', { current: { key: '', value: '' }, filter: this.appliedFilters, params: this.filterParams });
        this.$emit('clean');
      });
      this.noFiltersApplied = true;
    },
    rangeDragging(idx, v) {
      this.$set(this.f[idx].values[idx], 'value', v);
    },
    showMore(filterData) {
      if (Object.keys(filterData).length > 5) {
        return true;
      }
      return false;
    },
    expand(e) {
      const ul = e.target.previousElementSibling;
      if (e.target.classList.value.includes('show')) {
        e.target.classList.remove('show');
        e.target.classList.add('hide');
        e.target.previousElementSibling.classList.remove('show-more');
        ul.style.maxHeight = `${ul.scrollHeight}px`;
      } else {
        e.target.classList.remove('hide');
        e.target.classList.add('show');
        e.target.previousElementSibling.classList.add('show-more');
        ul.style.maxHeight = null;
        const topics = document.querySelector('.options');
        topics.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    },
  },
};
</script>

<style lang="scss">
.filters-accordion {
  color: var(--main-dark-shade-0);

  .head {
    margin-bottom: var(--spacing-36);
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: 500;
    font-size: 21px;
    line-height: 110%;
  }

  .filters-dropdown {
    > div {
      > div {
        display: flex;
        justify-content: space-between;
        align-items: center;
        cursor: pointer;
      }

      &:not(:last-of-type) {
        margin-bottom: var(--spacing-40);
      }

      .filter-name {
        font-weight: 500;
        line-height: 150%;
      }

      svg {
        flex: 0 0 auto;
      }

      &.open {
        .options {
          max-height: 220px;
        }

        > div {
          svg {
            transform: rotate(180deg);
          }
        }

        .show,
        .hide {
          display: block;
        }

        .search-options,
        .range-options {
          overflow: unset;
        }
      }

      &:not(.open) .options {
        max-height: 0 !important;
      }
    }

    .options {
      padding: 0;
      display: flex;
      flex-direction: column;
      list-style: none;
      margin: var(--spacing-12) 0 var(--spacing-8) 0;
      position: relative;
      overflow: hidden;
      transition: all var(--duration) ease-in;
      max-height: 0;

      li {
        span {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: block;
        }
      }

      &.range-options {
        cursor: unset;

        > div:first-child {
          display: flex;
          justify-content: space-between;
          width: 100%;

          input:not([type='range']) {
            padding: 6px 7px;
            height: 36px;
            max-width: 60px;
            font-size: 14px;
          }
        }

        .range-slider-wrapper {
          margin-top: 12px;
          width: 100%;

          .vue-slider {
            height: 8px !important;

            .vue-slider-process {
              background-color: var(--accent);
            }

            .vue-slider-dot-handle {
              background-color: var(--accent);
              box-shadow: none;
            }

            .vue-slider-rail {
              background-color: var(--main-light-shade-2);
            }
          }
        }
      }

      li {
        padding: var(--spacing-12);
        background: var(--main-light-shade-3);
        color: var(--main-dark-light-3);
        border-radius: 5px;
        margin-bottom: var(--spacing-4);
        cursor: pointer;
        height: 40px;
        display: flex;
        align-items: center;
        font-size: 14px;
        font-weight: 500;

        &:not(.not-capitalized) {
          text-transform: capitalize;
        }

        &:hover {
          background: var(--main-dark-shade-4);
        }

        &.selected {
          color: var(--accent-shade-3);
          background: var(--accent-light-3);
        }

        &.first {
          order: -1;
        }

        &.last {
          order: 100;
        }
      }

      &.show-more {
        li:nth-child(-n+5) {
          display: flex;
        }
      }

      .search-results {
        position: absolute;
        top: 92%;
        width: 100%;
        background: var(--main-light);
        z-index: 1;
        border-bottom-left-radius: 5px;
        border-bottom-right-radius: 5px;
        border: 1px solid var(--main-light-shade-2);
        max-height: 200px;
        overflow-y: auto;

        &::-webkit-scrollbar-track {
          background: var(--main-dark);
        }

        &::-webkit-scrollbar-thumb {
          background: var(--main-dark-light-3);
        }

        ul {
          list-style: none;
          padding: 0;
          margin: 0;

          li {
            padding: var(--spacing-8) var(--spacing-12);

            &.focused,
            &:hover {
              background-color: var(--main-light-shade-2);
              font-weight: 600;
            }
          }
        }
      }
    }

    .show,
    .hide {
      line-height: 100%;
      display: none;
    }

    .show:after {
      content: "Show more";
    }

    .hide:after {
      content: "Show less";
    }

    .show:after,
    .hide:after {
      font-weight: 600;
      font-size: 14px;
      line-height: 110%;
      color: var(--main-light-shade-4);
    }

    input:not([type='range']) {
      height: 40px;
      font-size: 14px;
      border-color: var(--main-light-shade-2);
      margin-top: 8px;

      &::-moz-placeholder { font-size: 14px; }
      &:-moz-placeholder { font-size: 14px; }
      &:-ms-input-placeholder { font-size: 14px; }
      &::-webkit-input-placeholder { font-size: 14px; }
    }
  }
}

.fa {
  .filters-accordion .filters-dropdown .options li {
    background: var(--main-light-shade-2-200);
    color: var(--main-dark-light-3);

    &:hover {
      background: var(--main-light-shade-3);
    }

    &.selected {
      color: var(--accent);
      background: var(--accent-light-2);
    }
  }
}
</style>
